import { createFileRoute } from "@tanstack/react-router";
import { InfoPage } from "@/components/InfoPage";

export const Route = createFileRoute("/disclaimer")({
  head: () => ({
    meta: [
      { title: "Disclaimer | Picamini.online" },
      {
        name: "description",
        content:
          "Read the Picamini.online disclaimer about educational information, random tool results, PDF processing, external links, and advertising.",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Disclaimer | Picamini.online" },
      { property: "og:description", content: "Important information about classroom tools, generated results, PDF processing, advertising, and external links." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://picamini.online/disclaimer" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Disclaimer | Picamini.online" },
      { name: "twitter:description", content: "Important limitations and responsible-use information for Picamini.online tools." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { property: "og:locale", content: "en_US" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/disclaimer" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/disclaimer" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/disclaimer" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://picamini.online/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Disclaimer",
              "item": "https://picamini.online/disclaimer"
            }
          ]
        })
      }
    ]
  }),
  component: DisclaimerPage,
});

function DisclaimerPage() {
  return (
    <InfoPage
      eyebrow="Important information"
      title="Disclaimer"
      description="Please read this page before relying on information, generated results, or processed documents from Picamini.online."
    >
      <p>
        <strong>Effective date: September 6, 2026</strong>
      </p>
      <h2>General information</h2>
      <p>
        Picamini.online provides general educational and productivity tools for convenience. The
        content on this site is not professional advice and should not replace guidance from a
        qualified teacher, administrator, attorney, accountant, healthcare professional, or other
        specialist.
      </p>
      <h2>Use in schools and classrooms</h2>
      <p>
        Educators and institutions are responsible for deciding whether a tool is appropriate for
        their students, curriculum, accessibility needs, privacy requirements, and local policies.
        Adults supervising children should explain safe and responsible use of online services and
        should avoid entering unnecessary personal information.
      </p>
      <h2>Random and generated results</h2>
      <p>
        Name pickers, wheels, generators, timers, games, and similar tools may use randomization or
        user-provided settings. Results are not guaranteed to be mathematically perfect, suitable
        for every situation, or appropriate for high-stakes decisions. Review results and apply
        your own judgment before using them.
      </p>
      <p>
        A random result is not a guarantee of fairness in every real-world context. If a selection
        affects grades, access, employment, money, safety, or another important outcome, use an
        appropriate process reviewed by the responsible decision-maker.
      </p>
      <h2>PDF and document tools</h2>
      <p>
        Browser-based PDF tools are provided as a convenience. File compatibility, formatting,
        fonts, encryption, signatures, and output quality can vary by document and browser. Keep
        an original copy of important files and verify the exported document before sharing or
        submitting it.
      </p>
      <p>
        Although many PDF operations are designed to run locally in your browser, browser behavior,
        file structure, fonts, and third-party libraries can affect results. Picamini.online does
        not guarantee that a file will be preserved, repaired, converted, signed, or encrypted in
        the exact way you expect.
      </p>
      <h2>External links and advertising</h2>
      <p>
        Picamini.online may display advertisements and link to third-party websites. We do not
        endorse every product, service, claim, or external resource shown through advertising or
        linked from the site. Third-party websites have their own terms and privacy policies.
      </p>
      <p>
        Advertising does not constitute an endorsement, recommendation, warranty, or partnership
        unless expressly stated. Review product details and the advertiser&apos;s terms before making
        a purchase or sharing information.
      </p>
      <h2>No guarantee</h2>
      <p>
        We make reasonable efforts to keep information current and tools functional, but we do not
        guarantee that the site will always be accurate, complete, secure, or available. Use the
        site at your own discretion and keep backups of information or files that matter to you.
      </p>
      <h2>Changes to this disclaimer</h2>
      <p>
        We may update this Disclaimer as the site, tools, or applicable requirements change. The
        current version and effective date are published on this page. Continued use of
        Picamini.online after an update indicates acceptance of the revised information.
      </p>
      <h2>Questions</h2>
      <p>
        For questions or to report an issue, contact{" "}
        <a href="mailto:support@picamini.online">support@picamini.online</a> or visit our{" "}
        <a href="/contact">Contact page</a>.
      </p>
    </InfoPage>
  );
}
